import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Autocomplete,
  TextField,
  Typography,
} from "@mui/material";
import theme from "theme";

import { Person } from "api/Community/CommunityInterfaces";
import AddPeoplePopup from "pages/community/People/AddPeoplePopup";
import { RectangularTextButton } from "components/Base/Buttons";

type Props = {
  open: boolean;
  title?: string;
  fetchPeople?: () => Promise<Person[]>;
  people?: Person[];
  loading?: boolean;
  selected: Person[];
  onSelectedChange: (value: Person[]) => void;
  disabledIds?: number[];
  confirmLabel?: string;
  onClose: () => void;
  onConfirm: () => void;
  getOptionLabel?: (p: Person) => string;
  helperText?: string;
};

const defaultLabel = (p: Person) =>
  `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || "Unnamed person";

const uniqById = (items: Person[]) =>
  Array.from(new Map(items.filter(Boolean).map((p) => [p.id, p])).values());

const PeoplePickerDialog: React.FC<Props> = ({
  open,
  title = "Choose People",
  fetchPeople,
  people,
  loading: loadingProp,
  selected,
  onSelectedChange,
  disabledIds = [],
  confirmLabel = "Save People",
  onClose,
  onConfirm,
  getOptionLabel = defaultLabel,
  helperText = "Select one or more people, remove to unlink, or add a new person.",
}) => {
  const [allPeople, setAllPeople] = React.useState<Person[]>([]);
  const [loadingInternal, setLoadingInternal] = React.useState(false);
  const [addOpen, setAddOpen] = React.useState(false);

  const disabledSet = React.useMemo(() => new Set(disabledIds), [disabledIds]);
  const effectiveLoading = loadingProp ?? loadingInternal;

  const loadPeople = React.useCallback(async () => {
    if (!fetchPeople) return;

    setLoadingInternal(true);

    try {
      const res = await fetchPeople();
      setAllPeople(res || []);
    } catch (e) {
      console.error("Failed to load people:", e);
      setAllPeople([]);
    } finally {
      setLoadingInternal(false);
    }
  }, [fetchPeople]);

  React.useEffect(() => {
    if (Array.isArray(people)) setAllPeople(people);
  }, [people]);

  React.useEffect(() => {
    if (!open) return;
    if (!fetchPeople) return;

    let alive = true;

    (async () => {
      setLoadingInternal(true);

      try {
        const res = await fetchPeople();
        if (alive) setAllPeople(res || []);
      } catch (e) {
        console.error("Failed to load people:", e);
        if (alive) setAllPeople([]);
      } finally {
        if (alive) setLoadingInternal(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, fetchPeople]);

  const handleNewPersonSaved = async (newPerson: any) => {
    setAddOpen(false);

    if (fetchPeople) {
      await loadPeople();
    }

    if (newPerson?.id) {
      onSelectedChange(uniqById([...selected, newPerson]));
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              backgroundColor: theme.custom.base.background.default.dark,
              color: theme.custom.base.text.primary.dark,
            },
          },
        }}
      >
        <DialogTitle>{title}</DialogTitle>

        <DialogContent
          sx={{
            backgroundColor: theme.custom.base.background.default.dark,
            color: theme.custom.base.text.primary.dark,
          }}
        >
          <Box
            sx={{
              mt: 1,
              mb: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              {helperText}
            </Typography>

            <RectangularTextButton
              colour="clear"
              onClick={() => setAddOpen(true)}
            >
              Add New
            </RectangularTextButton>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Autocomplete
              multiple
              loading={effectiveLoading}
              options={allPeople}
              getOptionDisabled={(p) => disabledSet.has(p.id)}
              value={selected}
              onChange={(_, value) => onSelectedChange(value)}
              getOptionLabel={getOptionLabel}
              renderInput={(params) => (
                <TextField {...params} label="Search People…" />
              )}
              sx={{
                backgroundColor: theme.custom.base.background.paper.dark,
                color: theme.custom.base.text.primary.dark,
              }}
            />
          </Box>

          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <RectangularTextButton colour="clear" onClick={onClose}>
              Cancel
            </RectangularTextButton>

            <RectangularTextButton
              colour="highlighted"
              onClick={onConfirm}
              disabled={selected.length === 0}
            >
              {confirmLabel}
            </RectangularTextButton>
          </Box>
        </DialogContent>
      </Dialog>

      <AddPeoplePopup
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={handleNewPersonSaved}
      />
    </>
  );
};

export default PeoplePickerDialog;